import { langLiteralType } from "@/modules/locales";
import React from "react";
import TTS from "@/components/TTS";
import { IYouWonModal, IGenericModal } from "./Modals";

export type GenericMouseClickVoid = (e: React.MouseEvent) => void;

export interface ILottoResultType {
	pcode: string;
	number: string;
	periode: number;
	submit: boolean;
	id: number;
	cmd: string;

	ballLeft?: string;
	ballRight?: string;
}

export interface IGameType {
	//to prevent overblasting from the Socket server
	isGameConnected: boolean;

	//Bet Limits
	max: number;
	max50: number;
	min: number;
	min50: number;

	//General
	name: string;
	category: string;
	pcode: string;
	periode: number;
	shoePeriode: string;

	// Video quality
	stream: string;
	stream_hd: string;
	//Unused for now
	stream_4k: string;
	stream_8k: string;

	// Lobby
	newgame: string; //"0" or "1" for some reason
	order: number;
	hover: boolean;

	roadmap: {big_road: any[]};
	history: any[];
	resultHistory: any[];

	historyBlink: boolean;

	//Display toggles
	lobbyRoadmap: boolean;
	lobbyHistory: boolean;
	lobbyMinMax: boolean;
	lobbyStatistics: boolean;

	//Desktop
	active: boolean;
	maximised: boolean;

	//Betting Panel
	totalBet: number;
	totalWin: number;
	lastSubmittedTotal: number;

	//Text-To-Speech class
	tts: TTS;

	//Chips on the betting panel
	bet: IKeyNumberDict;

	//Used in undo bet & clear bet -- bet insertion history
	betHistory: IBetHistory[];
	
	//Used in bet submission
	submitted: boolean;
	
	//Used in game time update
	hasBetCloseChecked: boolean;

	//Modals
	messageModal: IGenericModal;
	betConfirmedModal: IGenericModal;
	winModal: IYouWonModal;

	disallowInsertionAfterClickSubmit: boolean;

	streamObj: JSX.Element;

	gongBallResult?: IGongballResult;

	//Controllers
	disableUndoBet: boolean;
	disableClearBet: boolean;
	disableRebet: boolean;
	disableSubmitBet: boolean;

	// Machine Games
	activeBetGroup?: string;
	mustSubmitTickets?: boolean;
	tickets?: IRunningTicketType[];
	activeUnsubmittedTicketRow?: number;

	/**
	 * Used in machine games: e.g. Lotto, Shio Balls
	 */
	groupedUnsubmittedTickets?: IGroupedUnsubmittedTicket;

	/**
	 * Used in Pinball
	 */
	unsubmittedTickets?: IUnsubmittedTicket[];

	ticketCounts?: IKeyNumberDict;
	lastPeriodTickets?: IRunningTicketType[];
	lottoResult?: ILottoResultType;

	// BC/DT/SHF
	newSet: boolean;
	pushedBigRoad: boolean;
	enlargedRoadmap?: string;

	//V2 & V4 Games
	virtualCardScanResult?: any; //Todo: write the types: Baccarat, DT, I4S, SHF
	niuNiuResult?: any;

	//In-game props
	win: any; //Todo: write the types

	alwaysShowRoadmapText: boolean;
	activeRoadmapFilter: string;

	roadmapFilters: string[];
	roadmapText: {[key: string]: JSX.Element};

	//Sounds
	hasPlayedCloseBetSFX: boolean;

	// (Optional to use)
	maintenance: number | boolean;
	maintenance_text: string;

	betOutOfBounds: {[key: string]: boolean};
	transparentChips: boolean;

	fast_table: boolean;

	/**
	 * Used in Toto games
	 */
	link?: string;
}

export interface IGameTypeM42 extends Omit<IGameType, "groupedUnsubmittedTickets"> {
	unsubmittedTickets: IUnsubmittedTicket[];
}

export interface IGameObjectType {
	[pcode: string]: IGameType;
}

export interface ILanguageType {
	[txtKey: string]: string;
}

export interface IGenericGameResultType {
	win: string;
	pcode: string;
	timer: string; //should be number
	periode: number;

	game: string; //don't use this
}

//Userdata Types

export interface IUserDetailType {
	uid: number;
	device: string;
	lang: langLiteralType;
	balance: number; // "40100.00";
	currency: string;
	
	//Supposed to be booleans
	enableSound: string; // "true";
	enableMusic: string; 
	autoSubmitBet: string; 
	enableStreaming: string; 
	autoRebet: string; 
	enableRotation: string; 
	
	chipBase: number[];
	readonly nickname: string;
	displayNickname: string; //Mutated nickname (stripped from user agent)

	lastBet: any[]; //Todo: define this
	
	maingame: string;
	table: string;
	operatorId: string;
	
	currentBet: any;
	dateTime: string; // "2019-06-25T03:57:48.580Z";
	operatorLogo: string; //URL to the logo

	sessionToken: string;
    apiKey?: string;
    
	regionSocket: string;
    socketUrl?: string;
	token_sid?: string;
	lobbyUrl?: string;
}

export interface IUserdataType {
	user: IUserDetailType;
	game: {
		properties: IUserPropType[];
	};
}

export interface IUserPropType {
	type: string;
	position: string;
	link: string;
	status: number;
	content: string;
	lang?: string;
	datecreate: string;
}



// Bet Types
export interface IStandardBetProps {
	classes: string;

	betNum: string;
	betGroup: string;

	betType: JSX.Element | string;
	betLabel: JSX.Element | string;

	commonProps: IGameTableType;

	//for array keys
	_key?: string;

	disabled?: boolean;
}

export interface IStandardBetV4Props extends IStandardBetProps {
	winCondition?: boolean;
	betImage?: JSX.Element;
}



//Running Tickets
export interface IRunningTicketType {
	type: string;
	tebak: string;
	taruhan: number;
	periode: number;
}

//Unsubmitted Ticket
export enum ShioAnimals {
	rat,
	ox,
	tiger,
	rabbit,
	dragon,
	snake,
	horse,
	goat,
	monkey,
	rooster,
	dog,
	pig
}

export interface IUnsubmittedTicket {
	bet: number;

	//Race Ball
	numbers?: number[];

	//Shio Fights
	animals?: ShioAnimals[];
}

//Grouped Unsubmitted Tickets
export interface IGroupedUnsubmittedTicket {
	[group: string]: IUnsubmittedTicket[];
}

/**
 * Used in bet undo buffer
 */
export interface IBetHistory {
	//Old games
	key?: string;
	value?: number;

	//Machine games
	type?: string;
	number?: number;
	animal?: ShioAnimals;

	betGroup?: string;
	unsubmittedTicketIdx?: number;

	amount?: number;
	bet?: number;

	//Index relative to row
	animalIdx?: number;
	numberIdx?: number;
}

export interface ILastBetType {
	game: string;
	lastBet: ILastBetArrayType[];
	periode: number;
	tebak: string;
	type: string;
	taruhan: number;
}

export interface ILastBetArrayType {
	periode: number;
	tebak: string;
	type: string;
	taruhan: number;
}

// Set from loadNewValue data
export interface ITimerType {
	pcode: string;

	/**
	 * Time left (in seconds)
	 * When updating, don't use "BET CLOSED", instead use 0
	 */
	time: number;

	// max time
	timer: number;
}

export interface ITimerObjectType {
	[pcode: string]: ITimerType;
}

export interface IMaximisedGameProps {
	data: IGameType;
	userdata: IUserdataType;
	timerData: ITimerType;
	lang: ILanguageType;
	activeGameCount: number;
	chipBase: number[];

	showHiddenMultibet?: boolean;

	enableStreaming: boolean;
	isLoadingStreaming: boolean;

	activeGamePcodes?: string[];

	baccaratDarkMode?: boolean;

	completeTable: boolean;
	toggleCompleteTable: (e: React.MouseEvent) => void;

	insertBet: (e: any) => void;
	maximiseGame: (e: any) => void;
	restoreGames: (e: any) => void;
	closeGame: (e: any) => void;
	setLobbyRoadmapFilter: (e: any) => void;

	toggleLobbyHistory: (e: any) => void;
	toggleLobbyRoadmap: (e: any) => void;
	toggleGameStatistics: (e: any) => void;
	toggleLobbyMinMax: (e: any) => void;

	undoBet: (e: any) => void;
	clearBet: (e: any) => void;
	reBet: (e: any) => void;
	sendBet: (e: any) => void;

	clearModalsWithPcode: (e: any) => void;

	//Machine Games
	activateUnsubmittedTicketRow?: (e: any) => void;
	addAnimal?: (e: any) => void;
	addNumberM26?: (e: any) => void;
	addNumberM30?: (e: any) => void;
	addNumberM42?: (e: any) => void;
	addUnsubmittedTicketRow?: (e: any) => void;

	activateBetGroup?: (e: any) => void;

	removeBet?: (e: any) => void;
	removeAnimal?: (e: any) => void;
	removeNumberM26?: (e: any) => void;
	removeNumberM30?: (e: any) => void;
	removeNumberM42?: (e: any) => void;

	removeTicket?: (e: any) => void;

	// Video Quality support
	videoQuality: VideoQualityType;
	changeVideoQuality: () => void;
}

export interface IV2Props extends IMaximisedGameProps {
	autoHideHeader: boolean;

	toggleDarkMode: (e: React.ChangeEvent) => void;
	toggleEnlargeRoadmap: (e: React.MouseEvent) => void;
	updateSettings: (e: React.MouseEvent) => void;
}

export interface IV4Props extends IV2Props {
	showHiddenRoadmap: boolean;
	toggleHiddenRoadmap: (e: any) => void;
}

export interface ITotoProps {
	data: IGameType;
	userdata: IUserdataType;
	lang?: ILanguageType;
	maximiseGame?: (e: any) => void;
	activeGamePcodes?: string[];
	activeGameCount?: number;
	restoreGames?: (e: any) => void;
	closeGame?: (e: any) => void;
	sidemenuIcons?: (e: any) => void;
	updateUserData?: (e: any) => void;
	insufficientBalance?: boolean;

	showBurgerMenu?: boolean;
	toggleBurgerMenu?: (e: any) => void;
	openTransactionHistory?: (e: React.MouseEvent) => void;
	openResultHistory?: (e: React.MouseEvent) => void;
	toggleSettings?: (e: React.MouseEvent) => void;
	toggleHowToPlay?: (e: React.MouseEvent) => void;
	activeGame?: IGameType;
}

export interface IGameTableType {
	data: IGameType;
	langdata: ILanguageType;

	pcode: string;

	insertBet: (e: any) => void;
	chipBase: number[];

	//V1 Legacy
	isBetClosed?: boolean;
	win?: string;
	bets?: IKeyNumberDict;

	//Desktop
	maximised?: boolean;

	mobile?: boolean;
	portrait?: boolean;	

	betOutOfBounds?: {[key: string]: boolean};

	//Only for BC & DT
	v2?: boolean;
	completeTable?: boolean;

	//Machine Games
	activeBetGroup?: string;
	activeUnsubmittedTicketRow?: number;
	groupedUnsubmittedTickets?: IGroupedUnsubmittedTicket;
	unsubmittedTickets?: IUnsubmittedTicket[];

	activateBetGroup?: (e: any) => void;
	addNumberM26?: (e: any) => void;
	addAnimal?: (e: any) => void;
	removeAnimal?: (e: any) => void;
	addNumberM30?: (e: any) => void;

	addNumberM42?: (e: React.SyntheticEvent<HTMLDivElement>) => void;

	showHiddenMultibet?: boolean;
}

export interface ITotoGameTableType {
	data: IGameType;
	langdata: ILanguageType;
	pcode: string;
	maximised?: boolean;
	mobile?: boolean;
	portrait?: boolean;
	userdata: IUserdataType;
}

export interface INewGameTableType {
	activeGame: IGameType;
	langdata: ILanguageType;

	insertBet: (e: any) => void;
	chipBase: number[];

	activateBetGroup?: (e: any) => void;
	addNumberM26?: (e: any) => void;
	addAnimal?: (e: any) => void;
	removeAnimal?: (e: any) => void;
	addNumberM30?: (e: any) => void;
}



export interface IGongballResult {
	banker: string;
	player: string;
}


//Virtual Balls
export interface IVirtualBalls {
	lottoResult: ILottoResultType;
	activeGame: IGameType;
	langdata: ILanguageType;

	enableRotation?: boolean;
	betClosed?: boolean;
	showHugeStreaming?: boolean;
}



//How to Play
export interface IHowToPlayPageProps {
	userdata: IUserdataType;
	pcode?: string,
	langdata?: ILanguageType
}



//Mobile Component Types
export interface IV2V4MobileProps extends IVideoQualityProps {
	userdata: IUserdataType;
	timeText: number;
	activeGame: IGameType;
	lang: ILanguageType;

	// sidemenuIcons: string[];
	iphone: boolean;
	orientation: string;

	isLoadingStreaming: boolean;
	enableStreaming: boolean;

	insufficientBalance: boolean;

	completeTable: boolean;
	relaxed: boolean;
	toggleCompleteTable: (e: any) => void;
	
	clearModalsWithBind: (e: any) => void;

	insertBet: (e: any) => void;

	//Header items
	updateSettings: (e: any) => void;

	showBurgerMenu: boolean;
	toggleBurgerMenu: (e: any) => void;

	showExitToLobby: boolean;
	backToLobby: (e: any) => void;
	hideConfirmationPopup: (e: any) => void;

	roadmapExpanded: boolean;

	showMinMax: boolean;
	toggleMinMax: (e: any) => void;

	toggleHowToPlay: (e: any) => void;
	toggleRoadmapDisplay: (e: any) => void;

	toggleSettings: (e: any) => void;

	openResultHistory: (e: any) => void;
	openTransactionHistory: (e: any) => void;

	changeChip: (e: any) => void;
	chipValue: number;

	//Generic Controller functions
	undoBet: (e: any) => void;
	clearBet: (e: any) => void;
	reBet: (e: any) => void;
	sendBet: (e: any) => void;

	updateUserData?: (e: any) => void;
}



//Machine Game Types
export interface ITicketTableProps {
	activeGame: IGameType;
	langdata: ILanguageType;
	mobile: boolean;

	addUnsubmittedTicketRow: (e: any) => void;
	activateUnsubmittedTicketRow: (e: any) => void;
	insertBet: (e: any) => void;
	removeBet: (e: any) => void;

	removeAnimal?: (e: any) => void;
	removeNumberM30?: (e: any) => void;
}

export interface ITicketTableState {
	expandedRows: IKeyStringDict;
}


export interface IKeyAnyDict {
	[key: string]: any;
}

export interface IKeyStringDict {
	[key: string]: string;
}

export interface IKeyNumberDict {
	[key: string]: number;
}

export interface IKeyStringArrayDict {
	[key: string]: string[];
}

export interface IBaccaratVoiceSets {
	[pcode: string]: {
		lastPlayerMatch: number,
		lastBankerMatch: number
	};
}


// Video Quality
export interface IVideoQualityProps {
	videoQuality: VideoQualityType;
	changeVideoQuality: () => void;
}

export type VideoQualityType = "sd" | "hd";

// export enum VideoQualityEnum {
// 	SD,
// 	HD
// }


export type MouseEventVoid = (e: React.MouseEvent) => void;
